import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BackupDestinationDto,
  BackupDestinationType,
  BackupPlanPolicy,
  BackupPlanPolicyDto,
  S3StorageEntry,
} from '@cpm/shared';
import { BUILTIN_BACKUP_POLICY } from '@cpm/shared';
import { BookOpen, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useMe } from '../api/auth.js';
import {
  DEFAULT_SCHEDULE_VALUES,
  RESTORE_GUIDE_PATH,
  RestoreGuideLink,
  ScheduleFormFields,
  type ScheduleFormValues,
} from '../components/backups-panels.js';
import { Modal } from '../components/Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../components/ui.js';

/**
 * Backups module home (ADR-0015 + ADR-0042): legacy Coolify standalone
 * database schedules remain available while CPM adds whole-subscription
 * restic repositories and verified staging restores.
 */

function HowItWorksCard() {
  return (
    <Card>
      <CardHeader
        title="How backups work here"
        description="Two complementary layers protect databases and complete subscriptions."
      />
      <div className="space-y-3 px-5 py-4 text-sm text-slate-600 dark:text-slate-300">
        <p>
          Existing <strong>Coolify standalone-database schedules</strong> stay available on each
          database page. Subscription protection adds encrypted repositories containing persistent
          volumes, application-consistent database dumps, and portable CPM configuration.
        </p>
        <ul className="list-disc space-y-1 pl-5 text-slate-500 dark:text-slate-400">
          <li>
            Subscription restores always start in <strong>isolated staging</strong>, verify
            hashes, database integrity and row counts, then run configured health checks.
            Production overwrite requires a separate admin promotion and a fresh safety backup.
          </li>
          <li>
            Databases embedded in one-click <strong>services</strong> remain volume-only unless
            moved to a standalone database; that limitation is shown on each subscription.
          </li>
          <li>
            Operator-managed restic destinations may be server-local, S3-compatible, or SFTP.
            Tenants never supply personal FTP or cloud credentials.
          </li>
          <li>
            The legacy Coolify S3 registry below applies only to standalone-database schedules.
          </li>
        </ul>
        <Link
          to={RESTORE_GUIDE_PATH}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-500"
        >
          <BookOpen className="size-4" /> Open the restore guide
        </Link>
      </div>
    </Card>
  );
}

// ── Subscription protection repositories (admin) ────────────────────

interface DestinationDraft {
  name: string;
  type: BackupDestinationType;
  serverUuid: string;
  repository: string;
  repositoryPassword: string;
  metadataKey: string;
  transportSecret: string;
  bandwidthLimitKib: number;
}

function randomMetadataKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes));
}

function emptyDestination(): DestinationDraft {
  return {
    name: '',
    type: 'local',
    serverUuid: '',
    repository: '/var/lib/cpm-backups/repositories/',
    repositoryPassword: '',
    metadataKey: randomMetadataKey(),
    transportSecret: '',
    bandwidthLimitKib: 0,
  };
}

function keyStatusTone(status: BackupDestinationDto['metadataKeyStatus']) {
  return status === 'available' ? 'green' : status === 'missing' ? 'yellow' : 'red';
}

function SubscriptionDestinationsCard() {
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<DestinationDraft>(emptyDestination);
  const destinations = useQuery({
    queryKey: ['backups', 'destinations'],
    queryFn: () =>
      api.get<{ destinations: BackupDestinationDto[] }>('/api/backups/destinations'),
  });
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/backups/destinations', {
        ...draft,
        serverUuid: draft.serverUuid.trim() || null,
        transportSecret: draft.transportSecret.trim() || null,
        retention: {
          keepLast: 7,
          keepHourly: 0,
          keepDaily: 7,
          keepWeekly: 4,
          keepMonthly: 12,
          keepYearly: 3,
        },
        enabled: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups', 'destinations'] });
      setDraft(emptyDestination());
      setAdding(false);
    },
  });
  const toggle = useMutation({
    mutationFn: (destination: BackupDestinationDto) =>
      api.patch(`/api/backups/destinations/${destination.id}`, {
        enabled: !destination.enabled,
      }),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['backups', 'destinations'] }),
  });
  const remove = useMutation({
    mutationFn: (destination: BackupDestinationDto) =>
      api.delete(`/api/backups/destinations/${destination.id}`),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['backups', 'destinations'] }),
  });

  if (destinations.isPending) {
    return (
      <Card>
        <CardHeader title="Subscription backup destinations" />
        <Spinner />
      </Card>
    );
  }
  if (destinations.error) return <ErrorNote error={destinations.error} />;

  return (
    <Card>
      <CardHeader
        title="Subscription backup destinations"
        description="Restic-compatible repositories executed on each covered Coolify server. Repository, metadata, and transport credentials are write-only."
        actions={
          isAdmin ? (
            <Button type="button" onClick={() => setAdding(true)}>
              <Plus className="size-4" /> Add destination
            </Button>
          ) : undefined
        }
      />
      {destinations.data.destinations.length === 0 ? (
        <EmptyState
          title="No subscription destinations"
          description="An admin must configure a local, S3-compatible, or SFTP repository before protection can be scheduled."
        />
      ) : (
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
          {destinations.data.destinations.map((destination) => (
            <li key={destination.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 font-medium">
                  {destination.name}
                  <Badge tone={destination.enabled ? 'green' : 'gray'}>
                    {destination.enabled ? 'enabled' : 'disabled'}
                  </Badge>
                  <Badge tone="blue">{destination.type}</Badge>
                </p>
                <p className="mt-1 truncate font-mono text-xs text-slate-500 dark:text-slate-400">
                  {destination.repository}
                </p>
                <p className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                  <span>server: {destination.serverUuid ?? 'all'}</span>
                  <Badge tone={keyStatusTone(destination.repositoryPasswordStatus)}>
                    repository key {destination.repositoryPasswordStatus}
                  </Badge>
                  <Badge tone={keyStatusTone(destination.metadataKeyStatus)}>
                    metadata key {destination.metadataKeyStatus}
                  </Badge>
                </p>
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    loading={toggle.isPending}
                    onClick={() => toggle.mutate(destination)}
                  >
                    {destination.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="hover:!text-red-600"
                    aria-label={`Delete ${destination.name}`}
                    loading={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Delete "${destination.name}" from CPM? Repository data is not deleted.`,
                        )
                      ) {
                        remove.mutate(destination);
                      }
                    }}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {(toggle.error ?? remove.error) && (
        <div className="px-5 pb-4">
          <ErrorNote error={toggle.error ?? remove.error} />
        </div>
      )}

      <Modal
        title="Add subscription backup destination"
        open={adding}
        onClose={() => setAdding(false)}
      >
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="destination-name">Name</Label>
              <Input
                id="destination-name"
                required
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Primary offsite"
              />
            </div>
            <div>
              <Label htmlFor="destination-type">Type</Label>
              <Select
                id="destination-type"
                value={draft.type}
                onChange={(event) => {
                  const type = event.target.value as BackupDestinationType;
                  setDraft({
                    ...draft,
                    type,
                    repository:
                      type === 'local'
                        ? '/var/lib/cpm-backups/repositories/'
                        : type === 's3'
                          ? 's3:https://s3.example.test/bucket/path'
                          : 'sftp:backup@example.test:/repositories/name',
                  });
                }}
              >
                <option value="local">Server-local</option>
                <option value="s3">S3-compatible</option>
                <option value="sftp">SFTP</option>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="destination-server">
              Coolify server UUID {draft.type === 'local' ? '(required)' : '(blank = all servers)'}
            </Label>
            <Input
              id="destination-server"
              required={draft.type === 'local'}
              value={draft.serverUuid}
              onChange={(event) => setDraft({ ...draft, serverUuid: event.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="destination-repository">Restic repository</Label>
            <Input
              id="destination-repository"
              required
              className="font-mono"
              value={draft.repository}
              onChange={(event) => setDraft({ ...draft, repository: event.target.value })}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="destination-password">Repository password</Label>
              <Input
                id="destination-password"
                type="password"
                minLength={12}
                required
                value={draft.repositoryPassword}
                onChange={(event) =>
                  setDraft({ ...draft, repositoryPassword: event.target.value })
                }
              />
            </div>
            <div>
              <Label htmlFor="destination-bandwidth">Bandwidth limit (KiB/s, 0 = unlimited)</Label>
              <Input
                id="destination-bandwidth"
                type="number"
                min={0}
                value={draft.bandwidthLimitKib}
                onChange={(event) =>
                  setDraft({ ...draft, bandwidthLimitKib: Number(event.target.value) })
                }
              />
            </div>
          </div>
          <div>
            <Label htmlFor="destination-metadata-key">Metadata key (base64, 32 bytes)</Label>
            <Input
              id="destination-metadata-key"
              required
              className="font-mono"
              value={draft.metadataKey}
              onChange={(event) => setDraft({ ...draft, metadataKey: event.target.value })}
            />
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
              Copy this generated key to independent operator escrow before saving. CPM cannot
              recover secret metadata if both the panel copy and escrow are lost.
            </p>
          </div>
          {draft.type !== 'local' && (
            <div>
              <Label htmlFor="destination-transport">
                {draft.type === 's3'
                  ? 'S3 credentials JSON'
                  : 'SFTP OpenSSH private key (optional when host credentials are preconfigured)'}
              </Label>
              <Textarea
                id="destination-transport"
                required={draft.type === 's3'}
                rows={5}
                value={draft.transportSecret}
                onChange={(event) => setDraft({ ...draft, transportSecret: event.target.value })}
                placeholder={
                  draft.type === 's3'
                    ? '{"accessKeyId":"…","secretAccessKey":"…"}'
                    : '-----BEGIN OPENSSH PRIVATE KEY-----'
                }
              />
            </div>
          )}
          {create.error && <ErrorNote error={create.error} />}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create destination
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

// ── S3 destination registry (admin) ──────────────────────────────────

function S3StoragesCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['backups', 'settings'],
    queryFn: () => api.get<{ s3Storages: S3StorageEntry[] }>('/api/backups/settings'),
    retry: false,
  });
  const [uuid, setUuid] = useState('');
  const [label, setLabel] = useState('');

  const save = useMutation({
    mutationFn: (s3Storages: S3StorageEntry[]) =>
      api.put('/api/backups/settings', { s3Storages }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
      setUuid('');
      setLabel('');
    },
  });

  // Staff (403) — the registry is admin-only.
  if (settings.error) return null;
  if (settings.isPending) {
    return (
      <Card>
        <CardHeader title="S3 destinations" />
        <Spinner />
      </Card>
    );
  }
  const storages = settings.data.s3Storages;

  return (
    <Card>
      <CardHeader
        title="S3 destinations"
        description="Create the S3 storage in Coolify's UI (Storages), then register its uuid here so backup schedules can use it. The Coolify API cannot create or list S3 storages."
      />
      <div className="px-5 py-4">
        {storages.length === 0 ? (
          <p className="pb-3 text-sm text-slate-500 dark:text-slate-400">
            No S3 destinations registered — backups stay on the database's server only.
          </p>
        ) : (
          <ul className="mb-3 divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {storages.map((storage) => (
              <li key={storage.uuid} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium">{storage.label}</p>
                  <p className="truncate font-mono text-xs text-slate-500 dark:text-slate-400">
                    {storage.uuid}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  className="hover:!text-red-600"
                  aria-label={`Remove ${storage.label}`}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove "${storage.label}" from the registry? Existing schedules keep uploading to it; it just disappears from the picker.`,
                      )
                    ) {
                      save.mutate(storages.filter((s) => s.uuid !== storage.uuid));
                    }
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate([...storages, { uuid: uuid.trim(), label: label.trim() }]);
          }}
        >
          <div className="min-w-40 flex-1">
            <Label htmlFor="s3-label">Label</Label>
            <Input
              id="s3-label"
              required
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Hetzner offsite"
            />
          </div>
          <div className="min-w-52 flex-1">
            <Label htmlFor="s3-uuid">Coolify storage uuid</Label>
            <Input
              id="s3-uuid"
              required
              value={uuid}
              onChange={(e) => setUuid(e.target.value)}
              className="font-mono"
              placeholder="hg8kgqcko0o400c48o0kck4o"
            />
          </div>
          <Button type="submit" loading={save.isPending}>
            <Plus className="size-4" /> Register
          </Button>
        </form>
        {save.error != null && (
          <div className="mt-3">
            <ErrorNote error={save.error} />
          </div>
        )}
      </div>
    </Card>
  );
}

// ── Per-plan default policies ────────────────────────────────────────

function policyToFormValues(policy: BackupPlanPolicy): ScheduleFormValues {
  return {
    frequency: policy.frequency,
    enabled: policy.enabled,
    saveS3: policy.saveS3,
    s3StorageUuid: policy.s3StorageUuid ?? null,
    dumpAll: policy.dumpAll,
    retentionAmountLocally: policy.retentionAmountLocally,
    retentionDaysLocally: policy.retentionDaysLocally,
    retentionMaxStorageGbLocally: policy.retentionMaxStorageGbLocally,
    retentionAmountS3: policy.retentionAmountS3,
    retentionDaysS3: policy.retentionDaysS3,
    retentionMaxStorageGbS3: policy.retentionMaxStorageGbS3,
  };
}

function policySummary(policy: BackupPlanPolicy): string {
  const parts = [policy.frequency];
  if (policy.retentionAmountLocally > 0) parts.push(`keep ${policy.retentionAmountLocally}`);
  parts.push(policy.saveS3 ? 'S3 on' : 'local only');
  if (!policy.autoApply) parts.push('auto-apply off');
  return parts.join(' · ');
}

function PlanPolicyForm({
  entry,
  s3Storages,
  onDone,
}: {
  entry: BackupPlanPolicyDto;
  s3Storages: S3StorageEntry[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const initial = entry.policy ?? BUILTIN_BACKUP_POLICY;
  const [values, setValues] = useState<ScheduleFormValues>(policyToFormValues(initial));
  const [autoApply, setAutoApply] = useState(initial.autoApply);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/backups/plan-policies/${entry.planId}`, {
        ...values,
        s3StorageUuid: values.saveS3 ? values.s3StorageUuid : null,
        autoApply,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups', 'plan-policies'] });
      onDone();
    },
  });
  const reset = useMutation({
    mutationFn: () => api.delete(`/api/backups/plan-policies/${entry.planId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups', 'plan-policies'] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <label htmlFor="policy-autoapply" className="flex items-center gap-2 text-sm">
        <input
          id="policy-autoapply"
          type="checkbox"
          className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
          checked={autoApply}
          onChange={(e) => setAutoApply(e.target.checked)}
        />
        Apply automatically when a database is provisioned
      </label>
      <ScheduleFormFields
        values={values}
        onChange={setValues}
        s3Storages={s3Storages}
        idPrefix="policy"
      />
      {(save.error ?? reset.error) != null && <ErrorNote error={save.error ?? reset.error} />}
      <div className="flex justify-between gap-3">
        <Button
          type="button"
          variant="secondary"
          loading={reset.isPending}
          onClick={() => reset.mutate()}
        >
          Reset to built-in default
        </Button>
        <Button type="submit" loading={save.isPending}>
          Save policy
        </Button>
      </div>
    </form>
  );
}

function PlanPoliciesCard() {
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const [editing, setEditing] = useState<BackupPlanPolicyDto | null>(null);

  const policies = useQuery({
    queryKey: ['backups', 'plan-policies'],
    queryFn: () => api.get<{ policies: BackupPlanPolicyDto[] }>('/api/backups/plan-policies'),
    retry: false,
  });
  const settings = useQuery({
    queryKey: ['backups', 'settings'],
    queryFn: () => api.get<{ s3Storages: S3StorageEntry[] }>('/api/backups/settings'),
    retry: false,
    enabled: isAdmin,
  });

  if (policies.error) return <ErrorNote error={policies.error} />;
  if (policies.isPending) {
    return (
      <Card>
        <CardHeader title="Default backup policies" />
        <Spinner />
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Default backup policies"
        description="Per plan: the schedule created automatically when a database is provisioned and the plan includes backups. Plans without a stored policy use the built-in default (daily 03:00, keep 7 locally)."
      />
      {policies.data.policies.length === 0 ? (
        <EmptyState title="No plans yet" description="Create a plan first." />
      ) : (
        <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
          {policies.data.policies.map((entry) => (
            <li key={entry.planId} className="flex items-center justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-medium">
                  {entry.planName}
                  {!entry.featureEnabled && <Badge tone="gray">backups not in plan</Badge>}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {entry.policy ? policySummary(entry.policy) : 'Built-in default'}
                </p>
              </div>
              {isAdmin && (
                <Button
                  variant="ghost"
                  aria-label={`Edit policy for ${entry.planName}`}
                  onClick={() => setEditing(entry)}
                >
                  <Pencil className="size-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Modal
        title={`Default backup policy — ${editing?.planName ?? ''}`}
        open={editing !== null}
        onClose={() => setEditing(null)}
      >
        {editing && (
          <PlanPolicyForm
            entry={editing}
            s3Storages={settings.data?.s3Storages ?? []}
            onDone={() => setEditing(null)}
          />
        )}
      </Modal>
    </Card>
  );
}

export function BackupsPage() {
  return (
    <div>
      <PageHeader
        title="Backups"
        description="Restore-tested subscription protection plus existing Coolify standalone-database schedules"
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <HowItWorksCard />
        <S3StoragesCard />
      </div>
      <div className="mt-4">
        <SubscriptionDestinationsCard />
      </div>
      <div className="mt-4">
        <PlanPoliciesCard />
      </div>
    </div>
  );
}
